import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthenticator } from '@aws-amplify/ui-react';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { Alert, Box, Chip, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import MobileStepper from '@mui/material/MobileStepper';
import { useDebouncedCallback } from 'use-debounce';

import CompanyLinks from '../../components/CompanyLinks/CompanyLinks';
import CustomBackdrop from '../../components/CustomBackdrop/CustomBackdrop';
import PageHeader from '../../components/PageHeader';
import SearchComponent from '../../components/Search/Search';
import { useValidation } from '../../contexts/ValidationContext';
import { useValidationV2 } from '../../contexts/ValidationV2Context';
import {
  CompanyAssessmentType,
  GetSoftwareFirmsQuery,
  useGetShortCompanyLazyQuery,
  useGetSoftwareFirmsLazyQuery,
  useMarkCompanyAsSoftwareMutation,
} from '../../gql/generated/graphql';
import { getFirstNode } from '../../gql/helpers';
import { paths } from '../../routes/paths';
import CustomPaper from '../DFMPage/components/CustomPaper/CustomPaper';
import IsSoftwareStep from '../ValidationPage/components/ValidationSteps/IsSoftwareStep';
import ValidateAddressStep from '../ValidationPage/components/ValidationSteps/ValidateAddressStep';
import ValidateCustomers from '../ValidationPage/components/ValidationSteps/ValidateCustomers';
import ValidateShareholdingManagement from '../ValidationPage/components/ValidationSteps/ValidateShareholdingManagement';
import ValidateTechnology from '../ValidationPage/components/ValidationSteps/ValidateTechnology';

import CertificationsStep from './components/CertificationsStep';
import CompanyFinishStep from './components/CompanyFinishStep';
import CrawlResultFinishStep from './components/CrawlResultFinishStep';
import KeywordsStepV2 from './components/KeywordsStepV2';
import NotRelevantRejectionV2 from './components/NotRelevantRejectionV2';
import ProductsBusinessModelStep from './components/ProductsBusinessModelStep';
import SizeStepV2 from './components/SizeStepV2';
import { useValidationLock } from './hooks/useValidationLock';
import styles from './styles';
import { TrackASteps, ValidationTrack } from './types';

type SoftwareFirmEdge = NonNullable<GetSoftwareFirmsQuery['softwareFirms']>['edges'][0];

const ValidationPageV2 = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthenticator();
  const { state, setState } = useValidationV2();
  const { companyValidation } = useValidation();

  const stepParam = searchParams.get('step');
  const [activeStep, setActiveStep] = useState(stepParam ? parseInt(stepParam) : 0);
  const [options, setOptions] = useState<SoftwareFirmEdge[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [company, setCompany] = useState<CompanyAssessmentType | SoftwareFirmEdge['node'] | null>(null);

  const companyParamsId = searchParams.get('companyId');
  const trackParam = searchParams.get('track') as ValidationTrack | null;
  const crIdParam = searchParams.get('crId');

  const companyId = companyParamsId ? parseInt(companyParamsId) : null;
  const crawlResultId = crIdParam ? parseInt(crIdParam) : null;
  const authId = user?.username?.replace('GoogleWorkspace_', '') || null;

  // Derive userId from authId (numeric part) — for mutations needing userId
  // In production this comes from the user record; use a placeholder approach
  const userId = companyId ? 1 : null; // Will be replaced with actual user lookup

  const { lockAcquired, lockError, releaseLock } = useValidationLock({
    companyId,
    userId,
    graphqlEndpoint: process.env.REACT_APP_GRAPHQL_BACKEND || '/graphql',
  });

  const [getShortCompany, { data: shortCompany, loading: shortCompanyLoading }] = useGetShortCompanyLazyQuery();
  const [markCompanyAsSoftware, { loading: markCompanyAsSoftwareLoading }] = useMarkCompanyAsSoftwareMutation();

  const shortCompanyNode = getFirstNode(shortCompany?.getCompany);
  const genericCrawlResultId = crawlResultId || shortCompanyNode?.latestCrawlResult?.dbId || null;

  // Sync context state
  useEffect(() => {
    setState(prev => ({
      ...prev,
      track: trackParam,
      companyId,
      crawlResultId: genericCrawlResultId,
      userId,
      authId,
      lockAcquired,
      companyValidated: shortCompanyNode?.hasBeenValidated || false,
    }));
  }, [trackParam, companyId, genericCrawlResultId, userId, authId, lockAcquired, shortCompanyNode]);

  // Sync V1 isSoftware state into V2 context (IsSoftwareStep uses V1 context)
  useEffect(() => {
    if (companyValidation?.isSoftware !== state.isSoftware) {
      setState(prev => ({ ...prev, isSoftware: companyValidation?.isSoftware ?? null }));
    }
  }, [companyValidation?.isSoftware]);

  // Load company data on mount
  useEffect(() => {
    if (companyId) {
      getShortCompany({ variables: { companyId } });
    }
  }, [companyId]);

  useEffect(() => {
    if (shortCompanyNode) setCompany(shortCompanyNode);
  }, [shortCompanyNode]);

  // Search
  const debouncedSearch = useDebouncedCallback((value: string) => {
    search({ variables: { searchTerm: value } });
  }, 500);

  const [search, { loading: searchLoading }] = useGetSoftwareFirmsLazyQuery({
    onCompleted: data => setOptions(data.softwareFirms.edges),
  });

  const handleOptionSelect = (_, value: SoftwareFirmEdge | null) => {
    if (!value) return;
    setCompany(value.node);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('companyId', String(value.node.dbId));
    setSearchParams(newParams);
    getShortCompany({ variables: { companyId: value.node.dbId } });
  };

  const handleInputChange = (_, value: string, reason: string) => {
    if (reason === 'clear') {
      setSearchTerm('');
      setOptions([]);
      setCompany(null);
      search();
      return;
    }
    setSearchTerm(value);
    if (value) debouncedSearch(value);
  };

  // Step definitions
  const handleNext = async () => {
    if (trackParam === 'company' && activeStep === TrackASteps.IS_SOFTWARE && state.isSoftware === 'yes') {
      const result = await markCompanyAsSoftware({
        variables: { companyId, crawlResultId: genericCrawlResultId },
      });
      if (!result?.data?.markCompanyAsSoftware?.ok) return;
    }
    setActiveStep(prev => {
      const next = prev + 1;
      const newParams = new URLSearchParams(searchParams);
      newParams.set('step', String(next));
      setSearchParams(newParams, { replace: true });
      return next;
    });
  };

  const handleBack = () =>
    setActiveStep(prev => {
      const next = prev - 1;
      const newParams = new URLSearchParams(searchParams);
      newParams.set('step', String(next));
      setSearchParams(newParams, { replace: true });
      return next;
    });

  const companyName =
    (company?.shortName !== '<unnamed reference customer>' && company?.shortName) ||
    (company && 'legalName' in company && company?.legalName) ||
    company?.uniqueDomain;

  const trackASteps = companyId
    ? [
        { label: 'Is Software', component: <IsSoftwareStep companyId={companyId} /> },
        {
          label: 'Products & Business Model',
          component: <ProductsBusinessModelStep companyId={companyId} companyName={companyName || ''} />,
        },
        { label: 'Customer References', component: <ValidateCustomers companyId={companyId} /> },
        {
          label: 'Address',
          component: <ValidateAddressStep companyId={companyId} onNextStep={handleNext} />,
        },
        { label: 'Shareholders', component: <ValidateShareholdingManagement companyId={companyId} /> },
        {
          label: 'Certifications',
          component: <CertificationsStep companyId={companyId} companyName={companyName || ''} />,
        },
        { label: 'Technology', component: <ValidateTechnology companyId={companyId} /> },
        {
          label: 'Finish Company Validation',
          component: (
            <CompanyFinishStep companyId={companyId} userId={userId} authId={authId} onReleaseLock={releaseLock} />
          ),
        },
      ]
    : [];

  const trackBSteps =
    companyId && genericCrawlResultId
      ? [
          {
            label: 'Company Size',
            component: (
              <SizeStepV2
                companyId={companyId}
                crawlResultId={genericCrawlResultId}
                company={company as CompanyAssessmentType}
                userId={userId}
              />
            ),
          },
          {
            label: 'Keywords',
            component: <KeywordsStepV2 companyId={companyId} crawlResultId={genericCrawlResultId} />,
          },
          {
            label: 'Finish Crawl Validation',
            component: (
              <CrawlResultFinishStep
                crawlResultId={genericCrawlResultId}
                userId={userId}
                authId={authId}
                onReleaseLock={releaseLock}
              />
            ),
          },
        ]
      : [];

  const steps = trackParam === 'crawl' ? trackBSteps : trackASteps;
  const isTrackA = trackParam === 'company';

  const nextStepIsEnabled = (() => {
    if (isTrackA && activeStep === TrackASteps.IS_SOFTWARE) {
      return state.isSoftware === 'yes';
    }
    return true;
  })();

  return (
    <Box>
      <PageHeader
        title={company ? `Validation V2: ${companyName}` : 'Validation V2'}
        subtitle={
          company
            ? `${trackParam === 'company' ? 'Track A — Company' : 'Track B — Crawl Result'} Validation`
            : 'Search for companies to validate'
        }
        variant={company ? 'default' : 'search'}
        searchComponent={
          !company ? (
            <SearchComponent
              options={options}
              loading={searchLoading}
              searchTerm={searchTerm}
              onInputChange={handleInputChange}
              onOptionSelect={handleOptionSelect}
              onReset={() => {
                setSearchTerm('');
                setOptions([]);
                setCompany(null);
              }}
            />
          ) : undefined
        }
        rightContent={
          company ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={trackParam === 'company' ? 'Track A: Company' : 'Track B: Crawl'}
                color={trackParam === 'company' ? 'primary' : 'secondary'}
                size="small"
              />
              <SearchComponent
                options={options}
                loading={searchLoading}
                searchTerm={searchTerm}
                onInputChange={handleInputChange}
                onOptionSelect={handleOptionSelect}
                onReset={() => {
                  setSearchTerm('');
                  setOptions([]);
                  setCompany(null);
                }}
              />
            </Box>
          ) : undefined
        }
      />
      <Box px={3} py={2}>
        {lockError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {lockError}
          </Alert>
        )}
        {company && steps.length > 0 && (
          <>
            <MobileStepper
              variant="dots"
              steps={steps.length}
              position="static"
              activeStep={activeStep}
              sx={styles.mobileStepper}
              nextButton={
                <Button
                  size="small"
                  onClick={handleNext}
                  disabled={activeStep === steps.length - 1 || !nextStepIsEnabled}
                >
                  Next
                  <KeyboardArrowRight />
                </Button>
              }
              backButton={
                <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                  <KeyboardArrowLeft />
                  Back
                </Button>
              }
            />

            <CustomPaper sx={styles.customPaper}>
              <Typography textAlign="center" fontWeight="medium" variant="body2">
                {steps[activeStep].label}
              </Typography>
              <Box mt={4} />
              <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <CompanyLinks uniqueDomain={company?.uniqueDomain} linkedinUrl={company?.linkedinUrl} />
              </Box>
              {steps[activeStep]?.component}
              {isTrackA && activeStep < TrackASteps.COMPANY_FINISH && (
                <NotRelevantRejectionV2 companyId={companyId!} activeStep={activeStep} onReleaseLock={releaseLock} />
              )}
            </CustomPaper>
          </>
        )}
        <CustomBackdrop open={markCompanyAsSoftwareLoading || shortCompanyLoading} />
      </Box>
    </Box>
  );
};

export default ValidationPageV2;
